import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getVenues, createVenue, updateVenue, deleteVenue, toggleVenueFavorite, visitVenue, extractErrorMessage } from '../services/api';
import { FaPlus, FaEdit, FaTrash, FaStar, FaRegStar, FaMapMarkerAlt, FaFootballBall } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Venues.css';

function Venues({ user }) {
  const toast = useToast();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    world: '',
    district: '',
    ward: '',
    plot: '',
    venue_type: '',
    description: '',
    website_url: '',
    schedule: ''
  });

  useEffect(() => {
    fetchVenues();
  }, [filter]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { type: filter } : {};
      const data = await getVenues(params);
      setVenues(data.venues || data);
    } catch (error) {
      console.error('Error fetching venues:', error);
      toast.error(extractErrorMessage(error, 'Venues konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      if (editingVenue) {
        await updateVenue(editingVenue.id, formData);
      } else {
        await createVenue(formData);
      }
      resetForm();
      setShowModal(false);
      toast.success(editingVenue ? 'Venue aktualisiert' : 'Venue erstellt');
      fetchVenues();
    } catch (error) {
      console.error('Error saving venue:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern des Venues'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (venue) => {
    setEditingVenue(venue);
    setFormData({
      name: venue.name || '',
      world: venue.world || '',
      district: venue.district || '',
      ward: venue.ward || '',
      plot: venue.plot || '',
      venue_type: venue.venue_type || '',
      description: venue.description || '',
      website_url: venue.website_url || '',
      schedule: venue.schedule || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Venue wirklich löschen?')) return;
    try {
      await deleteVenue(id);
      toast.success('Venue gelöscht');
      fetchVenues();
    } catch (error) {
      console.error('Error deleting venue:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Venues'));
    }
  };

  const handleFavorite = async (id) => {
    try {
      await toggleVenueFavorite(id);
      fetchVenues();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Ändern des Favoriten-Status'));
    }
  };

  const handleVisit = async (id) => {
    try {
      await visitVenue(id);
      fetchVenues();
    } catch (error) {
      console.error('Error logging visit:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Loggen des Besuchs'));
    }
  };

  const resetForm = () => {
    setEditingVenue(null);
    setFormData({ name: '', world: '', district: '', ward: '', plot: '', venue_type: '', description: '', website_url: '', schedule: '' });
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const getTypeBadge = (type) => {
    const types = {
      club: { class: 'club', label: 'Club' },
      bar: { class: 'bar', label: 'Bar' },
      cafe: { class: 'cafe', label: 'Café' },
      restaurant: { class: 'restaurant', label: 'Restaurant' },
      venue: { class: 'venue', label: 'Venue' },
      other: { class: 'other', label: 'Sonstige' }
    };
    return types[type] || types.other;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaMapMarkerAlt /> Venues</h1>
          <button className="add-btn" onClick={openCreateModal}>
            <FaPlus /> Neues Venue
          </button>
        </div>

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'club' ? 'active' : ''} onClick={() => setFilter('club')}>Clubs</button>
          <button className={filter === 'bar' ? 'active' : ''} onClick={() => setFilter('bar')}>Bars</button>
          <button className={filter === 'cafe' ? 'active' : ''} onClick={() => setFilter('cafe')}>Cafés</button>
          <button className={filter === 'restaurant' ? 'active' : ''} onClick={() => setFilter('restaurant')}>Restaurants</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="venues-list">
            {venues.length > 0 ? (
              venues.map(venue => {
                const typeBadge = getTypeBadge(venue.venue_type);
                return (
                  <div key={venue.id} className={`venue-card ${venue.is_favorite ? 'favorite' : ''}`}>
                    <div className="venue-header">
                      <div className="venue-title-row">
                        <h3 className="venue-name">{venue.name}</h3>
                        <span className={`type-badge ${typeBadge.class}`}>{typeBadge.label}</span>
                      </div>
                      <button
                        className="favorite-btn"
                        onClick={() => handleFavorite(venue.id)}
                        title={venue.is_favorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                      >
                        {venue.is_favorite ? <FaStar className="starred" /> : <FaRegStar />}
                      </button>
                    </div>
                    <div className="venue-location">
                      <FaMapMarkerAlt />
                      <span>
                        {venue.world && `${venue.world}`}
                        {venue.district && ` - ${venue.district}`}
                        {venue.ward && `, Ward ${venue.ward}`}
                        {venue.plot && `, Plot ${venue.plot}`}
                      </span>
                    </div>
                    {venue.description && <p className="venue-desc">{venue.description}</p>}
                    {venue.schedule && (
                      <div className="venue-schedule">
                        <span className="schedule-label">Zeiten:</span> {venue.schedule}
                      </div>
                    )}
                    <div className="venue-meta">
                      {venue.visit_count !== undefined && (
                        <span className="visit-count">{venue.visit_count} Besuche</span>
                      )}
                      {venue.website_url && (
                        <a href={venue.website_url} target="_blank" rel="noopener noreferrer" className="venue-link">Website</a>
                      )}
                    </div>
                    <div className="venue-actions">
                      <button className="action-btn visit" onClick={() => handleVisit(venue.id)} title="Besuch loggen">
                        <FaFootballBall /> Besucht
                      </button>
                      <button className="action-btn edit" onClick={() => handleEdit(venue)} title="Bearbeiten">
                        <FaEdit />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(venue.id)} title="Löschen">
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-venues">Keine Venues gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingVenue ? 'Venue bearbeiten' : 'Neues Venue'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Welt</label>
                    <input
                      type="text"
                      value={formData.world}
                      onChange={e => setFormData({...formData, world: e.target.value})}
                      placeholder="z.B. Omega"
                    />
                  </div>
                  <div className="form-group">
                    <label>Typ</label>
                    <select
                      value={formData.venue_type}
                      onChange={e => setFormData({...formData, venue_type: e.target.value})}
                    >
                      <option value="">-- Wählen --</option>
                      <option value="club">Club</option>
                      <option value="bar">Bar</option>
                      <option value="cafe">Café</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="venue">Venue</option>
                      <option value="other">Sonstige</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bezirk</label>
                    <input
                      type="text"
                      value={formData.district}
                      onChange={e => setFormData({...formData, district: e.target.value})}
                      placeholder="z.B. Goblet"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ward</label>
                    <input
                      type="text"
                      value={formData.ward}
                      onChange={e => setFormData({...formData, ward: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Plot</label>
                  <input
                    type="text"
                    value={formData.plot}
                    onChange={e => setFormData({...formData, plot: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Website-URL</label>
                  <input
                    type="text"
                    value={formData.website_url}
                    onChange={e => setFormData({...formData, website_url: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Öffnungszeiten</label>
                  <input
                    type="text"
                    value={formData.schedule}
                    onChange={e => setFormData({...formData, schedule: e.target.value})}
                    placeholder="z.B. Fr-Sa 21:00-01:00 ST"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert…' : (editingVenue ? 'Speichern' : 'Erstellen')}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Venues;
